import {Injectable, NgZone, Optional} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslationService} from '@ngmedax/translation';
import FroalaEditor from 'froala-editor';
import {TRANSLATION_EDITOR_SCOPE} from '../../constants';
import {QuestionnaireStateService} from '../services/questionnaire-state.service';
import {QuestionnaireVariablesService} from '../services/questionnaire-variables.service';
import {QuestionnaireConditionService} from '../services/questionnaire-condition.service';
import {buildTemplateVariables} from './template-variables';
import {variableInsertionSpacing} from './template-variable-tokens';
import {conditionExpression} from './template-conditions';
import {ConditionBlock, CONDITION_TOKEN_SELECTOR, createConditionToken, decorateConditionTokens, findConditionBlocks,
  serializeConditionTokens} from './template-condition-tokens';
import {ConditionVariable, TemplateConditionEditorComponent, TemplateConditionResult}
  from '../components/template-condition-editor/template-condition-editor.component';

/** Connects visual condition boundaries with a typed editor and Froala's native history. */
@Injectable()
export class FroalaTemplateConditionsService {
  private modal: NgbModalRef = null;
  private activeEditor: any = null;

  /** Registers the shared command while retaining each editor's state in its own event handlers. */
  public constructor(private modals: NgbModal, private state: QuestionnaireStateService,
    private variables: QuestionnaireVariablesService, private conditions: QuestionnaireConditionService,
    private zone: NgZone, @Optional() private translation: TranslationService) {
    const froala = FroalaEditor as any;
    if (froala.COMMANDS.qaInsertCondition) { return; }
    const label = document.createElement('span');
    label.textContent = this.translate('Bedingung');
    froala.DefineIcon('qaInsertCondition', {NAME: '<span>⑂</span>' + label.outerHTML, template: 'text'});
    froala.RegisterCommand('qaInsertCondition', {
      title: 'Insert condition', focus: true, undo: false,
      /** Dispatches to the service instance that owns the active editor. */
      callback: function() { this.events.trigger('qa.insertCondition'); }
    });
    if (froala.LANGUAGE.de) { froala.LANGUAGE.de.translation['Insert condition'] = 'Bedingung einfügen'; }
  }

  /** Installs insertion, editing and deletion protection only for opted-in property editors. */
  public configure(options: any, locale: () => string): void {
    options.toolbarButtons.qaVariables.buttons.push('qaInsertCondition');
    options.toolbarButtons.qaVariables.buttonsVisible = 2;
    const self = this;
    const originalGet = options.events['html.get'];
    options.events['html.get'] = function(html: string) {
      const serialized = serializeConditionTokens(html);
      return originalGet ? originalGet.call(this, serialized) ?? serialized : serialized;
    };
    options.events['qa.insertCondition'] = function() { self.zone.run(() => self.open(this, locale())); };
    const originalPaste = options.events['paste.before'];
    options.events['paste.before'] = function(event: ClipboardEvent) {
      const block = self.partialBoundary(this, event);
      if (block) {
        self.zone.run(() => self.open(this, locale(), block.id));
        return false;
      }
      return originalPaste && originalPaste.call(this, event);
    };
    const originalInit = options.events.initialized;
    const originalDestroy = options.events.destroy;
    let detach: () => void;
    options.events.initialized = function() {
      const editor = this;
      /** Opens a clicked boundary without requiring the caret to be inside its protected text. */
      const edit = (event: CustomEvent): void => {
        event.stopPropagation();
        self.zone.run(() => self.open(editor, locale(), event.detail.id));
      };
      /** Rejects edits that would remove only part of a condition's structural markers. */
      const guard = (event: InputEvent | ClipboardEvent | KeyboardEvent | DragEvent): void => {
        if (event instanceof KeyboardEvent && event.key !== 'Backspace' && event.key !== 'Delete') { return; }
        if (event.type === 'dragstart' && (event.target as Element).closest(CONDITION_TOKEN_SELECTOR)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const block = self.partialBoundary(editor, event);
        if (!block) { return; }
        event.preventDefault();
        event.stopImmediatePropagation();
        self.zone.run(() => self.open(editor, locale(), block.id));
      };
      editor.el.addEventListener('qa-condition-edit', edit);
      ['beforeinput', 'cut', 'keydown', 'dragstart'].forEach(name => editor.el.addEventListener(name, guard, true));
      detach = () => {
        editor.el.removeEventListener('qa-condition-edit', edit);
        ['beforeinput', 'cut', 'keydown', 'dragstart'].forEach(name => editor.el.removeEventListener(name, guard, true));
      };
      originalInit && originalInit.call(editor);
    };
    options.events.destroy = function() {
      detach?.();
      if (self.activeEditor === this) { self.activeEditor = null; self.modal?.dismiss(); }
      originalDestroy && originalDestroy.call(this);
    };
  }

  /** Resolves the existing block before opening a modal and preserves the original selection. */
  private open(editor: any, locale: string, id?: string): void {
    if (this.modal || editor.codeView?.isActive()) { return; }
    const block = id === undefined ? this.partialBoundary(editor, {} as InputEvent) :
      findConditionBlocks(editor.el.textContent).find(item => item.id === id);
    if (id !== undefined && !block) { return; }
    editor.selection.save();
    editor.tooltip.hide();
    this.activeEditor = editor;
    const modal = this.modals.open(TemplateConditionEditorComponent, {
      centered: true, size: 'lg', windowClass: 'qa-template-condition-modal', ariaLabelledBy: 'qa-condition-title'
    });
    this.modal = modal;
    const component = modal.componentInstance as TemplateConditionEditorComponent;
    component.variables = this.catalog(locale);
    if (block) {
      component.condition = block.condition;
      component.unless = block.unless;
      component.otherwise = !!block.otherwise;
      component.hadOtherwise = !!block.otherwise;
      component.existing = true;
    }
    modal.result.then(result => this.finish(editor, locale, block, result), () => this.finish(editor, locale, block));
  }

  /** Reuses the existing runtime format mapping, including patient custom fields and question answers. */
  private catalog(locale: string): ConditionVariable[] {
    return buildTemplateVariables(this.state.getQuestionnaire(), this.variables.getVariableScopes(), locale, text => this.translate(text))
      .map(variable => {
        const format = variable.group === 'scoring' ? 'numeric' :
          variable.path === 'date' ? 'date' : this.conditions.getContainerForVariable(variable.path)?.format;
        return {...variable, valueType: format === 'numeric' ? 'number' :
          format === 'boolean' ? 'boolean' : format === 'date' ? 'date' : 'text'} as ConditionVariable;
      });
  }

  /** Restores focus on cancellation or commits one structural change as one native undo step. */
  private finish(editor: any, locale: string, block: ConditionBlock, result?: TemplateConditionResult): void {
    const connected = this.activeEditor === editor && editor.el?.isConnected;
    this.modal = null;
    this.activeEditor = null;
    if (!connected) { return; }
    editor.events.focus();
    editor.selection.restore();
    if (!result) { return; }
    const expression = result.condition && conditionExpression(result.condition);
    if (!result.unwrap && !expression) { return; }
    editor.undo.saveStep();
    const opening = `{{#${result.unless ? 'unless' : 'if'} ${expression}}}`;
    const closing = `{{/${result.unless ? 'unless' : 'if'}}}`;
    let insertedOpening: HTMLElement = null;
    if (block) {
      const tokens = Array.from(editor.el.querySelectorAll(CONDITION_TOKEN_SELECTOR)) as HTMLElement[];
      const owned = tokens.filter(token => token.dataset.qaConditionId === block.id);
      const first = owned.find(token => token.dataset.qaCondition === 'if');
      const last = owned.find(token => token.dataset.qaCondition === 'end');
      if (!first || !last) { return; }
      if (result.unwrap) {
        owned.forEach(token => token.replaceWith(document.createTextNode('')));
      } else {
        first.textContent = opening;
        first.title = opening;
        last.textContent = closing;
        last.title = closing;
        if (result.otherwise && !block.otherwise) {
          last.before(document.createTextNode('{{else}} '));
        }
      }
    } else {
      const range: Range = editor.selection.ranges()[0];
      const fragment = range.cloneContents();
      const holder = document.createElement('div');
      holder.appendChild(fragment);
      // selected content becomes the then branch, and a collapsed caret receives an editable gap
      const content = holder.innerHTML || ' ';
      const source = createConditionToken({start: 0, end: opening.length, source: opening, kind: 'if'}, 'new', '');
      const before = document.createRange();
      before.selectNodeContents(editor.el);
      before.setEnd(range.startContainer, range.startOffset);
      const spacing = variableInsertionSpacing(before.toString(), '');
      const end = document.createElement('span');
      end.textContent = (result.otherwise ? '{{else}} ' : '') + closing;
      editor.html.insert(spacing.prefix + source.outerHTML + content + end.innerHTML + '&nbsp;');
      insertedOpening = editor.el.querySelector('[data-qa-condition-id="new"]');
    }
    const labels: {[path: string]: string} = {};
    this.catalog(locale).forEach(variable => labels[variable.path] = variable.label);
    decorateConditionTokens(editor.el, labels, text => this.translate(text));
    if (insertedOpening?.isConnected) {
      const range = document.createRange();
      range.setStartAfter(insertedOpening);
      range.collapse(true);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
    editor.undo.saveStep();
    editor.events.trigger('contentChanged');
  }

  /** Detects partial marker deletion but allows removing an entire block in a normal text selection. */
  private partialBoundary(editor: any, event: InputEvent | ClipboardEvent | KeyboardEvent | DragEvent): ConditionBlock {
    const selection = window.getSelection();
    if (!selection.rangeCount) { return null; }
    let range = selection.getRangeAt(0);
    const target = (event as InputEvent).getTargetRanges?.()[0];
    if (target) {
      range = document.createRange();
      range.setStart(target.startContainer, target.startOffset);
      range.setEnd(target.endContainer, target.endOffset);
    }
    if (!editor.el.contains(range.commonAncestorContainer)) { return null; }
    const measure = document.createRange();
    measure.selectNodeContents(editor.el);
    measure.setEnd(range.startContainer, range.startOffset);
    let start = measure.toString().length;
    measure.setEnd(range.endContainer, range.endOffset);
    let end = measure.toString().length;
    if (start === end) {
      const inputType = (event as InputEvent).inputType || '';
      if ((event as KeyboardEvent).key === 'Backspace' || /Backward$/.test(inputType)) { start--; }
      else if ((event as KeyboardEvent).key === 'Delete' || /Forward$/.test(inputType)) { end++; }
      else { return null; }
    }
    return findConditionBlocks(editor.el.textContent).find(block => {
      if (start <= block.opening.start && end >= block.closing.end) { return false; }
      return [block.opening, block.otherwise, block.closing].filter(Boolean)
        .some(marker => start < marker.end && end > marker.start);
    });
  }

  /** Uses administration translations independently of the questionnaire text locale. */
  private translate(text: string): string {
    return this.translation ? this.translation.translate({text, scope: TRANSLATION_EDITOR_SCOPE}) : text;
  }
}
